// Angular
import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
// Rxjs
import { Subject, Observable, of, concat } from 'rxjs';
import { distinctUntilChanged, switchMap, tap, map } from 'rxjs/operators';
// Libs
import { ToastrService } from 'ngx-toastr';
// Models
import { IArticle } from '@core/models-v2/cms/special-reponse.interface';
import { Articulo } from '../../interfaces/articulos.response';
// Services
import { ArticleService } from '@core/services-v2/article.service';

@Component({
  selector: 'app-producto-select-busqueda',
  templateUrl: './producto-select-busqueda.component.html',
  styleUrls: ['./producto-select-busqueda.component.scss'],
})
export class ProductoSelectBusquedaComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() multiple = true;
  @Input() placeholder = 'Escriba 3 o más caracteres';
  @Input() nuevosArticulos: any[] = [];
  @Output() articulosSeleccionados = new EventEmitter<any>();

  articuloLoading = false;
  articuloInput$ = new Subject<string>();
  articulosSelect$!: Observable<any[]>;

  // Para p-autoComplete
  selectedItem: any = null;
  suggestions: any[] = [];
  articulos: Articulo[] = [];

  constructor(
    private logisticService: ArticleService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.buscarArticulo();
  }

  /**
   * Método para manejar la búsqueda del autoComplete
   */
  onSearch(event: any): void {
    const query = event.query;
    if (query && query.length >= 3) {
      this.articuloLoading = true;
      this.logisticService.slimSearch(query).subscribe({
        next: (response: any) => {
          // El servicio puede retornar un objeto con una propiedad que contiene el array
          // o directamente un array. Necesitamos verificar la estructura
          if (response && Array.isArray(response)) {
            this.suggestions = response;
          } else if (
            response &&
            response.results &&
            Array.isArray(response.results)
          ) {
            this.suggestions = response.results;
          } else if (
            response &&
            response.data &&
            Array.isArray(response.data)
          ) {
            this.suggestions = response.data;
          } else {
            this.suggestions = [];
          }
          this.articuloLoading = false;
        },
        error: () => {
          this.suggestions = [];
          this.articuloLoading = false;
        },
      });
    } else {
      this.suggestions = [];
    }
  }

  /**
   * Método cuando se selecciona un item
   */
  onItemSelect(event: any): void {
    console.log('onItemSelect llamado con:', event);

    if (!event) {
      console.log('Evento vacío en onItemSelect');
      return;
    }

    // En p-autoComplete, el evento puede venir de diferentes formas
    let selectedArticle = event;

    // Si viene como objeto con value
    if (event.value) {
      selectedArticle = event.value;
    }

    console.log('Producto seleccionado procesado:', selectedArticle);

    if (selectedArticle && selectedArticle.sku) {
      // Emitir el producto seleccionado directamente
      console.log('Emitiendo producto:', selectedArticle);
      this.articulosSeleccionados.emit(selectedArticle);

      // Limpiar la selección para permitir seleccionar otro producto
      setTimeout(() => {
        this.selectedItem = null;
        console.log('Input limpiado');
      }, 100);
    } else {
      console.log('Producto inválido o sin SKU:', selectedArticle);
    }
  }

  /**
   * Método cuando se limpia la selección
   */
  onClear(): void {
    this.selectedItem = null;
    this.suggestions = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['nuevosArticulos'] &&
      this.nuevosArticulos &&
      Array.isArray(this.nuevosArticulos)
    ) {
      if (this.hayCambios()) {
        if (this.nuevosArticulos.length === 0) {
          this.articulos = [];
        } else if (this.nuevosArticulos.length > 0) {
          // Vienen todos los atributos
          const search = this.nuevosArticulos.map((x) => x.sku).join(',');
          this.articuloInput$.next(search);
          this.articulos = [...this.nuevosArticulos];
        } else {
          this.toast.success(
            'El articulo no se pudo encontrar, por favor ingrese intente mas tardes'
          );
        }
      }
    }
  }

  hayCambios(): boolean {
    if (
      !this.articulos &&
      this.nuevosArticulos &&
      this.nuevosArticulos.length > 0
    ) {
      return true;
    }
    if (this.articulos.length !== this.nuevosArticulos.length) {
      return true;
    }
    const skus = this.nuevosArticulos.map((a) => a.sku);
    for (let i = 0; i < this.articulos.length; i++) {
      if (!skus.includes(this.articulos[i].sku)) {
        return true;
      }
    }
    return false;
  }

  buscarArticulo(): void {
    // Mantener el observable para la funcionalidad existente si es necesaria
    this.articulosSelect$ = concat(
      of([]),
      this.articuloInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.articuloLoading = true)),
        switchMap((search) =>
          this.logisticService.slimSearch(search).pipe(
            map((response: any) => {
              // Manejar la respuesta del servicio de la misma manera
              if (response && Array.isArray(response)) {
                return response;
              } else if (
                response &&
                response.results &&
                Array.isArray(response.results)
              ) {
                return response.results;
              } else if (
                response &&
                response.data &&
                Array.isArray(response.data)
              ) {
                return response.data;
              } else {
                return [];
              }
            }),
            tap(() => (this.articuloLoading = false))
          )
        )
      )
    );
  }

  compararArticulo(a: any, b: any): boolean {
    return a?.sku === b?.sku;
  }

  articulosCambiados(): void {
    this.articulosSeleccionados.emit(this.articulos);
  }

  ngOnDestroy(): void {
    if (this.articuloInput$) {
      this.articuloInput$.complete();
    }
  }
}
